/**
 * Vincent #113/#114/#115 (Sprint 4) — Messagerie hub.
 * Vincent #18 batch FLHQ (Eric 2026-02-06) — 3e onglet "Accès utilisateurs"
 * (matrice cases à cocher, admin/founder uniquement).
 */
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Hash, ChatCircleDots, ShieldCheck } from "@phosphor-icons/react";
import AgoraPage from "./AgoraPage";
import ChannelsPanel from "../components/messaging/ChannelsPanel";
import ChannelAccessMatrixPanel from "../components/messaging/ChannelAccessMatrixPanel";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { canEditContent } from "../utils/permissions";

export default function MessagingPage() {
  const { lang } = useLang();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Vincent #18/#280 — l'onglet "Accès utilisateurs" est visible pour les rôles
  // pouvant éditer le contenu. Eric 2026-06-25 (iter117 B2) — migré vers
  // canEditContent (single source of truth aligné avec backend require_editor).
  const canManageAccess = canEditContent(user);

  // Tab choice persisted in URL ?tab=channels|conversations|access.
  const initial = (() => {
    const t = new URLSearchParams(location.search).get("tab");
    if (t === "conversations") return "conversations";
    if (t === "access" && canManageAccess) return "access";
    return "channels";
  })();
  const [tab, setTab] = useState(initial);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("tab") !== tab) {
      params.set("tab", tab);
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    // Vincent B548 v3 (Eric 2026-07-24) — Correction définitive de la hauteur
    // de la messagerie. Vincent : « tu remontes de manière à masquer les noms
    // des 3 onglets... il faut réduire la hauteur totale de la fenêtre ».
    //
    // Stratégie bulletproof :
    //  1. On borne la hauteur du composant à `calc(100dvh - 180px)` — 180px
    //     couvre : top navbar (56) + p-4 wrapper (32) + watermark footer (~95)
    //     avec une marge de sécurité (~30) pour zoom ou bandeau simulation.
    //  2. On ajoute `overflow-hidden` — même si le contenu déborde, le
    //     navigateur NE PEUT PLUS scroller la page pour masquer les onglets.
    //  3. Les enfants (ChannelsPanel/AgoraPage) utilisent `h-full flex flex-col`
    //     et remplissent exactement l'espace disponible : plus de calc()
    //     dupliqué en interne, plus de risque d'overflow imprévisible.
    <div
      className="flex flex-col h-[calc(100dvh-180px)] overflow-hidden"
      data-testid="messaging-page-container"
    >
      <div className="flex items-center gap-1 border-b border-zinc-200 bg-white px-3 sm:px-6 shrink-0">
        <button
          onClick={() => setTab("channels")}
          className={`relative inline-flex items-center gap-1.5 px-3 py-3 text-sm font-medium transition-colors ${
            tab === "channels" ? "text-[#002FA7]" : "text-zinc-500 hover:text-zinc-800"
          }`}
          data-testid="messaging-tab-channels"
        >
          <Hash size={15} weight="bold" />
          {lang === "fr" ? "Canaux" : "Channels"}
          {tab === "channels" && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-[#002FA7] rounded-full" />}
        </button>
        <button
          onClick={() => setTab("conversations")}
          className={`relative inline-flex items-center gap-1.5 px-3 py-3 text-sm font-medium transition-colors ${
            tab === "conversations" ? "text-[#002FA7]" : "text-zinc-500 hover:text-zinc-800"
          }`}
          data-testid="messaging-tab-conversations"
        >
          <ChatCircleDots size={15} weight="bold" />
          {lang === "fr" ? "Conversations" : "Conversations"}
          {tab === "conversations" && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-[#002FA7] rounded-full" />}
        </button>
        {/* Vincent #18 batch FLHQ — 3e onglet visible aux admins/founders uniquement */}
        {canManageAccess && (
          <button
            onClick={() => setTab("access")}
            className={`relative inline-flex items-center gap-1.5 px-3 py-3 text-sm font-medium transition-colors ${
              tab === "access" ? "text-[#002FA7]" : "text-zinc-500 hover:text-zinc-800"
            }`}
            data-testid="messaging-tab-access"
          >
            <ShieldCheck size={15} weight="bold" />
            {lang === "fr" ? "Accès utilisateurs" : "User access"}
            {tab === "access" && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-[#002FA7] rounded-full" />}
          </button>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        {tab === "channels" && <ChannelsPanel />}
        {tab === "conversations" && <AgoraPage />}
        {tab === "access" && canManageAccess && <ChannelAccessMatrixPanel />}
      </div>
    </div>
  );
}
